import { WorkSurfaceSize } from "../../../game/bench-work/workpiece";
import { lerp } from "../../../utils/mathUtils";

/**
 * The geometry of the bench view's two trays — the shelf under the
 * bench and the arms — as pure screen math, so the drawing and the
 * hit-tests read one layout and a unit test can pin it down.
 *
 * A tray is a ledge along the bottom of the screen, anchored at its
 * outer edge (the shelf's left edge on the left, the arms' right edge
 * on the right) and growing inward. At rest it is compact: a label, and
 * the pieces on it as small tiles lapped over one another like boards
 * on a shelf. Hovered, it fans out (`openness` 1) into one slot per
 * space it has, empty slots included — the rail's rule, that the hooks
 * are the slot count, so room is visible without reading a number. The
 * two states are one layout at two values of `openness`, and the tiles
 * spring between them on their own holders. A carried piece brought
 * near opens it the same way, with the slot it would land in lit
 * (`landingSlot`), so where it is going is in view before it is let go.
 */

export type TrayKind = "shelf" | "arms";

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Where a tray hangs: its outer edge and the bottom of its ledge. */
export interface TrayAnchor {
  readonly kind: TrayKind;
  readonly edgeX: number;
  readonly bottomY: number;
}

export interface TrayLayout {
  readonly kind: TrayKind;
  readonly ledge: Rect;
  /** One slot per space, in order from the label inward. A slot past
   * `count` is empty; it only shows as the tray opens. */
  readonly slots: ReadonlyArray<Rect>;
  /** Where the label's outer end sits (its left for the shelf, its
   * right for the arms), and its vertical middle. */
  readonly labelX: number;
  readonly labelY: number;
}

/** The ledge's height. */
export const TRAY_LEDGE_H = 48;
/** A slot's box: wide, because stock lies along it. */
export const TRAY_SLOT_W = 76;
export const TRAY_SLOT_H = 36;
const TRAY_SLOT_GAP = 6;
const TRAY_PAD = 10;
/** How far apart lapped tiles sit at rest. */
const TRAY_REST_PITCH = 16;
/** Open, the slots run in rows of this many, stacked up from the ledge:
 * a run of seven would reach across the screen into the other tray. */
export const TRAY_COLUMNS = 4;
const TRAY_ROW_GAP = 6;
/** Room kept around a tile inside its slot. */
const TRAY_TILE_PAD = 6;
/** How far past the ledge the bare hand still counts as over the
 * tray — forgiving, the ledge is thin. */
export const TRAY_REACH = 14;
/** How far past the ledge a carried piece counts as over the tray: the
 * tray opens as the piece comes near, and once open its ledge has grown
 * under the hand, so it stays open until the hand really leaves. */
export const TRAY_DROP_REACH = 32;

export function trayLayout(
  anchor: TrayAnchor,
  opts: {
    readonly capacity: number;
    readonly count: number;
    /** 0 at rest, 1 fanned open. */
    readonly openness: number;
    readonly labelWidth: number;
  },
): TrayLayout {
  const { capacity, count, openness, labelWidth } = opts;
  // At rest every tile laps along one row; open, the slots stand in
  // rows of TRAY_COLUMNS, the ledge growing upward to hold them. Each
  // slot is the blend of its two places.
  const cols = Math.min(capacity, TRAY_COLUMNS);
  const rows = capacity > 0 ? Math.ceil(capacity / TRAY_COLUMNS) : 1;
  const openPitch = TRAY_SLOT_W + TRAY_SLOT_GAP;
  const rowPitch = TRAY_SLOT_H + TRAY_ROW_GAP;
  const runRest = count > 0 ? TRAY_SLOT_W + (count - 1) * TRAY_REST_PITCH : 0;
  const runOpen = cols > 0 ? TRAY_SLOT_W + (cols - 1) * openPitch : 0;
  const run = lerp(runRest, runOpen, openness);
  const width =
    TRAY_PAD + labelWidth + (run > 0 ? TRAY_PAD + run : 0) + TRAY_PAD;
  const height = TRAY_LEDGE_H + (rows - 1) * rowPitch * openness;
  const inward = anchor.kind === "shelf" ? 1 : -1;
  const left = anchor.kind === "shelf" ? anchor.edgeX : anchor.edgeX - width;
  const ledge: Rect = {
    x: left,
    y: anchor.bottomY - height,
    width,
    height,
  };
  const labelX = anchor.edgeX + inward * TRAY_PAD;
  // The run starts past the label and grows inward; a slot's x is its
  // left edge whichever way the run goes. The bottom row sits where the
  // ledge's one row sits at rest; rows above it climb as the tray opens.
  const runStart = labelX + inward * (labelWidth + TRAY_PAD);
  const bottomRowY = anchor.bottomY - (TRAY_LEDGE_H + TRAY_SLOT_H) / 2;
  const slots: Rect[] = [];
  for (let i = 0; i < capacity; i++) {
    const col = i % TRAY_COLUMNS;
    const row = Math.floor(i / TRAY_COLUMNS);
    const along = lerp(
      runStart + inward * i * TRAY_REST_PITCH,
      runStart + inward * col * openPitch,
      openness,
    );
    slots.push({
      x: anchor.kind === "shelf" ? along : along - TRAY_SLOT_W,
      y: bottomRowY - row * rowPitch * openness,
      width: TRAY_SLOT_W,
      height: TRAY_SLOT_H,
    });
  }
  return {
    kind: anchor.kind,
    ledge,
    slots,
    labelX,
    labelY: anchor.bottomY - TRAY_LEDGE_H / 2,
  };
}

/** The ledge plus its reach: where the hand counts as over it. */
export function trayZone(layout: TrayLayout, reach = TRAY_REACH): Rect {
  return grow(layout.ledge, reach);
}

/**
 * Which slot a piece let go over the tray would show up in. One lifted
 * off this very tray goes back into the gap it left (`liftedIndex`);
 * anything else lands after the last tile from its table or an earlier
 * one — the tray pools the run's shelves in table order, so a piece put
 * onto one table's shelf shows up among that table's tiles. `orders`
 * is each tile's table order, in slot order; the arms have one table.
 */
export function landingSlot(
  orders: ReadonlyArray<number>,
  ontoOrder: number,
  liftedIndex: number | null,
): number {
  if (liftedIndex !== null) return liftedIndex;
  let index = 0;
  for (const order of orders) if (order <= ontoOrder) index++;
  return index;
}

export function pointInRect(rect: Rect, x: number, y: number): boolean {
  return (
    x >= rect.x &&
    y >= rect.y &&
    x <= rect.x + rect.width &&
    y <= rect.y + rect.height
  );
}

/** The middle of a rect. */
export function rectCenter(rect: Rect): { x: number; y: number } {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

/**
 * Which tile is under a point: the filled slots, last laid on top —
 * lapped tiles at rest overlap, and the one showing most is the one
 * the hand finds.
 */
export function tileAt(
  layout: TrayLayout,
  count: number,
  x: number,
  y: number,
): number | null {
  for (let i = Math.min(count, layout.slots.length) - 1; i >= 0; i--) {
    if (pointInRect(layout.slots[i], x, y)) return i;
  }
  return null;
}

/**
 * How much a piece shrinks to be a tile: fitted into a slot with its
 * long side along the slot's length, relative to the stage's own scale
 * — and never grown, so a small offcut in a tray is no bigger than on
 * the bench.
 */
export function tileScale(
  size: WorkSurfaceSize,
  pxPerIn: number,
  /** Room kept around the tile; a squarish icon (a tool) reads better
   * given nearly the whole slot. */
  pad: number = TRAY_TILE_PAD,
): number {
  const longPx = Math.max(size.widthIn, size.heightIn) * pxPerIn;
  const shortPx = Math.min(size.widthIn, size.heightIn) * pxPerIn;
  return Math.min(
    (TRAY_SLOT_W - pad * 2) / longPx,
    (TRAY_SLOT_H - pad * 2) / shortPx,
    1,
  );
}

/** The room around a tool's icon in its slot — tighter than stock's,
 * since a square icon at stock's padding is a speck. */
export const TOOL_TILE_PAD = 2;

/** A tile lies with its long side along the slot: a board, longer than
 * it is wide, turns a quarter to do so. */
export function tileAngleDeg(size: WorkSurfaceSize): number {
  return size.heightIn > size.widthIn ? 90 : 0;
}

function grow(rect: Rect, by: number): Rect {
  return {
    x: rect.x - by,
    y: rect.y - by,
    width: rect.width + by * 2,
    height: rect.height + by * 2,
  };
}
